import { h, Host } from "@stencil/core";
import { getUniqueId } from "../../utils/unique-id";
/**
 * @slot - contents of the textarea element (alternative for the value prop)
 * @slot (description) - an additional description for the user, additional to the label
 */
export class TextareaField {
    constructor() {
        this.label = "";
        this.labelInfo = "";
        this.value = undefined;
        this.textareaId = "";
        this.name = "";
        this.placeholder = "";
        this.disabled = false;
        this.required = false;
        this.error = false;
        this.errorMsg = "";
        this.autocomplete = null;
    }
    textareaElement;
    setTextareaId() {
        this.textareaId = this.textareaId || getUniqueId();
    }
    componentWillLoad() {
        this.setTextareaId();
    }
    /** Emitted when a keyboard input occurred. */
    uxInput;
    /** Emitted when the value has changed. */
    uxChange;
    /** textareaField event, includes name, value and textareaFieldId */
    textareaFieldChange;
    handleInput = (e) => {
        this.value = this.textareaElement.value;
        this.uxInput.emit();
        this.textareaFieldChange.emit({
            value: e.target.value,
            name: this.name,
            textareaFieldId: this.textareaId
        });
    };
    handleChange = () => {
        this.value = this.textareaElement.value;
        this.uxChange.emit();
    };
    render() {
        const baseClass = "ux-textarea-field";
        const { label, labelInfo, value, textareaId, name, placeholder, disabled, required, error, errorMsg, handleChange, handleInput } = this;
        return (h(Host, { key: '13405d92b722538415fe50117877dbfe9c36f9c7', class: baseClass }, h("ux-input-label", { key: '73812e91d9852cda8d1a099faf421bea44fbf122', required: required, text: label, labelInfo: labelInfo, error: error, errorMsg: errorMsg, inputId: textareaId }), h("slot", { key: 'd3502527a22ad8aadb771f16f4976e653e548a78', name: "description" }), h("textarea", { key: 'aaa59b330b2b9d50387be0074c466d92cd9fa55e', class: {
                [`ux-textarea`]: true,
                [`ux-textarea--disabled`]: disabled,
                [`ux-textarea--error`]: error
            }, id: textareaId, name: name, value: value, placeholder: placeholder, disabled: disabled, required: required, "aria-invalid": error ? "true" : "false",
            // @ts-expect-error Check for typing
            ref: (el) => (this.textareaElement = el),
            //@ts-expected-error Stencil Bug https://github.com/ionic-team/stencil/issues/2882
            autocomplete: this.autocomplete,
            // @ts-expect-error Check for typing
            onInput: (e) => handleInput(e),
            // @ts-expect-error Check for typing
            onChange: (e) => handleChange(e) }, h("slot", { key: '8751c7346d80d3ee4e0d4b669c7a29ac1f1f11b5' }))));
    }
    static get is() { return "ux-textarea-field"; }
    static get originalStyleUrls() {
        return {
            "$": ["textarea-field.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["textarea-field.css"]
        };
    }
    static get properties() {
        return {
            "label": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "label for the the textarea field"
                },
                "attribute": "label",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "labelInfo": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "additional info label for  the input field"
                },
                "attribute": "label-info",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "value": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "value of the textarea element\ndefault value must be undefined - otherwise the default slot does not work as expected"
                },
                "attribute": "value",
                "reflect": false
            },
            "textareaId": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "textarea id attribute"
                },
                "attribute": "textarea-id",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "name": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "name of the textarea field"
                },
                "attribute": "name",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "placeholder": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "placeholder for the textarea field"
                },
                "attribute": "placeholder",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "disabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "indicates if the textarea field is disabled"
                },
                "attribute": "disabled",
                "reflect": false,
                "defaultValue": "false"
            },
            "required": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "indicates if the textarea field is required"
                },
                "attribute": "required",
                "reflect": false,
                "defaultValue": "false"
            },
            "error": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "indicates if the textarea field has an error state"
                },
                "attribute": "error",
                "reflect": false,
                "defaultValue": "false"
            },
            "errorMsg": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "error message that is rendered if component has an error"
                },
                "attribute": "error-msg",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "autocomplete": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "autocomplete attribute, will be set on the input element. Refer to HTML standards for allowed values"
                },
                "attribute": "autocomplete",
                "reflect": false,
                "defaultValue": "null"
            }
        };
    }
    static get events() {
        return [{
                "method": "uxInput",
                "name": "uxInput",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Emitted when a keyboard input occurred."
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }, {
                "method": "uxChange",
                "name": "uxChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Emitted when the value has changed."
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }, {
                "method": "textareaFieldChange",
                "name": "uxTextareaFieldChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "textareaField event, includes name, value and textareaFieldId"
                },
                "complexType": {
                    "original": "{\n        name: string;\n        value: string;\n        textareaFieldId: string;\n    }",
                    "resolved": "{ name: string; value: string; textareaFieldId: string; }",
                    "references": {}
                }
            }];
    }
    static get watchers() {
        return [{
                "propName": "textareaId",
                "methodName": "setTextareaId"
            }];
    }
}
//# sourceMappingURL=textarea-field.js.map
